import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarClock, ChevronLeft, ChevronRight, Loader2, Users, MapPin,
  ExternalLink, Sparkles, Clock, CheckCircle2,
} from "lucide-react";
import { T, tr } from "../lib/i18n";
import { PendingConfirmationAlert } from "../components/PendingConfirmationAlert";
import AppBottomBar from "../components/AppBottomBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCALE = { it: "it-IT", en: "en-GB", es: "es-ES", fr: "fr-FR", de: "de-DE" };
const X = {
  pickSlot: { it: "Seleziona uno slot", en: "Select a slot", es: "Selecciona un turno", fr: "Sélectionnez un créneau", de: "Wählen Sie einen Termin" },
  nameReq: { it: "Nome richiesto", en: "Name required", es: "Nombre obligatorio", fr: "Nom requis", de: "Name erforderlich" },
  contactReq: { it: "Email o telefono richiesti", en: "Email or phone required", es: "Email o teléfono obligatorio", fr: "Email ou téléphone requis", de: "E-Mail oder Telefon erforderlich" },
  bookErr: { it: "Errore prenotazione", en: "Booking error", es: "Error de reserva", fr: "Erreur de réservation", de: "Buchungsfehler" },
  received: { it: "Richiesta ricevuta", en: "Request received", es: "Solicitud recibida", fr: "Demande reçue", de: "Anfrage erhalten" },
  backHome: { it: "Torna alla home", en: "Back to home", es: "Volver al inicio", fr: "Retour à l'accueil", de: "Zur Startseite" },
  organizerInfo: { it: "Info dell'organizzatore", en: "Organizer info", es: "Info del organizador", fr: "Infos de l'organisateur", de: "Infos des Veranstalters" },
  pickDay: { it: "Scegli il giorno", en: "Choose a day", es: "Elige el día", fr: "Choisissez le jour", de: "Tag wählen" },
  pickSlotH: { it: "Scegli lo slot", en: "Choose a slot", es: "Elige el turno", fr: "Choisissez le créneau", de: "Termin wählen" },
  noSlots: { it: "Nessuno slot disponibile per questa data.", en: "No slots available for this date.", es: "No hay turnos disponibles para esta fecha.", fr: "Aucun créneau disponible à cette date.", de: "Keine Termine an diesem Datum." },
  seats: { it: "posti", en: "seats", es: "plazas", fr: "places", de: "Plätze" },
  confirmSlot: { it: "Conferma slot", en: "Confirm slot", es: "Confirmar turno", fr: "Confirmer le créneau", de: "Termin bestätigen" },
  name: { it: "Nome", en: "Name", es: "Nombre", fr: "Nom", de: "Name" },
  phone: { it: "Telefono", en: "Phone", es: "Teléfono", fr: "Téléphone", de: "Telefon" },
  participants: { it: "Partecipanti", en: "Participants", es: "Participantes", fr: "Participants", de: "Teilnehmer" },
  maxAvail: { it: "Massimo", en: "Max", es: "Máximo", fr: "Maximum", de: "Maximal" },
  available: { it: "disponibili", en: "available", es: "disponibles", fr: "disponibles", de: "verfügbar" },
  notes: { it: "Note (opzionale)", en: "Notes (optional)", es: "Notas (opcional)", fr: "Notes (facultatif)", de: "Hinweise (optional)" },
  confirmBooking: { it: "Conferma prenotazione", en: "Confirm booking", es: "Confirmar reserva", fr: "Confirmer la réservation", de: "Buchung bestätigen" },
};

export default function ExperiencePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lang] = useState(() => localStorage.getItem("lang") || "it");
  const t = (k) => tr(X[k], lang);
  const locale = LOCALE[lang] || "it-IT";
  const [event, setEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", participants: 1, note: "" });
  const [booking, setBooking] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    axios.get(`${API}/experiences/${id}`)
      .then((r) => { setEvent(r.data); setSelectedDate(r.data.active_from); })
      .catch(() => navigate("/"));
  }, [id, navigate]);

  useEffect(() => {
    if (!event || !selectedDate) return;
    setSlot(null);
    axios.get(`${API}/experiences/${id}/slots?date=${selectedDate}`)
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]));
  }, [event, selectedDate, id]);

  const availableDates = useMemo(() => {
    if (!event) return [];
    const out = [];
    const from = new Date(event.active_from + "T00:00:00");
    const to = new Date(event.active_to + "T00:00:00");
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      // Use LOCAL date components, not toISOString() which shifts to UTC
      // and can move the day backward by 1 in positive timezones.
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      out.push(`${y}-${m}-${day}`);
    }
    return out;
  }, [event]);

  const submit = async (e) => {
    e.preventDefault();
    if (!slot) return toast(t("pickSlot"));
    if (!form.name.trim()) return toast(t("nameReq"));
    if (!form.email && !form.phone) return toast(t("contactReq"));
    setBooking(true);
    try {
      const { data } = await axios.post(`${API}/experiences/${id}/bookings`, {
        slot_datetime: slot.start,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        participants: parseInt(form.participants) || 1,
        note: form.note,
      });
      setSuccess(data.booking);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("bookErr"));
    } finally { setBooking(false); }
  };

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7ebdf]">
        <Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#f7ebdf] p-4">
        <div className="max-w-lg mx-auto pt-12 text-center">
          <div className="inline-flex h-16 w-16 rounded-full bg-green-100 items-center justify-center mb-4">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="font-serif text-2xl font-bold text-[#2b1410] mb-2">{t("received")}</h1>
          <p className="text-sm text-[#7c5a4e] mb-1">{event.title}</p>
          <p className="text-sm text-[#4a1c17] font-semibold">
            {new Date(success.slot_datetime).toLocaleString(locale, { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}
          </p>
          {success.email && <p className="text-xs text-[#7c5a4e] mt-3 italic">{tr(T.bookingEmailSent, lang)} {success.email}</p>}
          <div className="text-left">
            <PendingConfirmationAlert lang={lang} />
          </div>
          <button onClick={() => navigate("/")} className="mt-6 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-5 py-2.5 text-sm font-semibold">
            {t("backHome")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7ebdf] pb-16">
      {/* Header with image */}
      <div className="relative">
        {event.image_url ? (
          <img src={event.image_url} alt="" className="w-full h-52 sm:h-72 object-cover" />
        ) : (
          <div className="w-full h-52 sm:h-72 bg-gradient-to-br from-red-700 via-red-600 to-red-800 flex items-center justify-center">
            <Sparkles className="h-20 w-20 text-amber-200/50" />
          </div>
        )}
        <button onClick={() => navigate(-1)} data-testid="exp-back-btn" className="absolute top-4 left-4 h-9 w-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md hover:bg-white transition-colors">
          <ChevronLeft className="h-5 w-5 text-[#4a1c17]" />
        </button>
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 text-white">
          <div className="max-w-2xl mx-auto">
            <span className="inline-block text-[10px] uppercase tracking-widest font-black bg-amber-300 text-red-900 px-2 py-0.5 rounded-full mb-1.5">
              {event.organizer}
            </span>
            <h1 className="font-serif text-2xl sm:text-3xl font-bold leading-tight">{event.title}</h1>
            {event.subtitle && <p className="text-sm sm:text-base italic text-amber-100/90 mt-1">{event.subtitle}</p>}
          </div>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 py-5">
        {event.description && (
          <p className="text-sm text-[#3a1f18] leading-relaxed whitespace-pre-line mb-5">{event.description}</p>
        )}

        <div className="flex items-center gap-3 text-xs text-[#7c5a4e] flex-wrap mb-5">
          <span className="inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> {event.active_from === event.active_to ? new Date(event.active_from + "T00:00:00").toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" }) : `${event.active_from} → ${event.active_to}`}</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {event.day_start_time}–{event.day_end_time}</span>
          {event.seats_per_slot > 1 && <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {event.seats_per_slot} pax/slot</span>}
          {event.price > 0 && <span className="font-bold text-[#4a1c17]">· {event.price} €</span>}
        </div>

        {event.external_url && (
          <a href={event.external_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#4a1c17] underline mb-5">
            <ExternalLink className="h-3 w-3" /> {t("organizerInfo")}
          </a>
        )}

        {/* Date selector */}
        {availableDates.length > 1 && (
          <div className="mb-5">
            <h3 className="text-[10px] uppercase tracking-widest font-bold text-[#7c5a4e] mb-2">{t("pickDay")}</h3>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {availableDates.map((d) => {
                const active = d === selectedDate;
                const dObj = new Date(d + "T00:00:00");
                return (
                  <button
                    key={d}
                    onClick={() => setSelectedDate(d)}
                    className={`shrink-0 rounded-lg border px-3 py-2 text-center min-w-[70px] transition-colors ${active ? "bg-[#4a1c17] border-[#4a1c17] text-white" : "bg-white border-[#e8dcc8] text-[#4a1c17] hover:bg-[#faf3ee]"}`}
                    data-testid={`exp-date-${d}`}
                  >
                    <div className="text-[10px] uppercase font-bold">{dObj.toLocaleDateString(locale, { weekday: "short" })}</div>
                    <div className="text-lg font-bold">{dObj.getDate()}</div>
                    <div className="text-[10px]">{dObj.toLocaleDateString(locale, { month: "short" })}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Slots */}
        <div className="mb-5">
          <h3 className="text-[10px] uppercase tracking-widest font-bold text-[#7c5a4e] mb-2">{t("pickSlotH")}</h3>
          {slots.length === 0 ? (
            <p className="text-sm italic text-[#7c5a4e]">{t("noSlots")}</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {slots.map((s) => {
                const active = slot?.start === s.start;
                return (
                  <button
                    key={s.start}
                    disabled={s.sold_out}
                    onClick={() => setSlot(s)}
                    data-testid={`exp-slot-${s.hhmm}`}
                    className={`rounded-lg py-2.5 px-2 text-sm font-bold transition-colors ${
                      s.sold_out ? "bg-gray-100 text-gray-400 line-through cursor-not-allowed"
                        : active ? "bg-[#4a1c17] text-white ring-2 ring-amber-500"
                        : "bg-white border border-[#e8dcc8] text-[#4a1c17] hover:bg-[#faf3ee]"
                    }`}
                  >
                    {s.hhmm}
                    <div className="text-[9px] font-normal mt-0.5">
                      {s.sold_out ? "sold out" : s.seats_available < s.seats_total ? `${s.seats_available}/${s.seats_total}` : `${s.seats_total} ${t("seats")}`}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Booking form */}
        {slot && (
          <form onSubmit={submit} className="rounded-2xl border border-[#e8dcc8] bg-white p-4 space-y-3" data-testid="exp-booking-form">
            <h3 className="font-serif text-lg font-bold text-[#2b1410]">
              {t("confirmSlot")} · {new Date(slot.start).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}
            </h3>
            <div>
              <label className="block text-[10px] uppercase tracking-widest font-semibold text-[#7c5a4e] mb-1">{t("name")} *</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-[#e8dcc8] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4a1c17]" data-testid="exp-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-widest font-semibold text-[#7c5a4e] mb-1">{t("phone")}</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-[#e8dcc8] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4a1c17]" data-testid="exp-phone" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest font-semibold text-[#7c5a4e] mb-1">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-[#e8dcc8] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4a1c17]" data-testid="exp-email" />
              </div>
            </div>
            {event.seats_per_slot > 1 && (
              <div>
                <label className="block text-[10px] uppercase tracking-widest font-semibold text-[#7c5a4e] mb-1">{t("participants")}</label>
                <input type="number" min="1" max={slot.seats_available} value={form.participants} onChange={(e) => setForm({ ...form, participants: e.target.value })} className="w-full border border-[#e8dcc8] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4a1c17]" data-testid="exp-participants" />
                <p className="text-[10px] text-[#7c5a4e] mt-0.5">{t("maxAvail")} {slot.seats_available} {t("available")}</p>
              </div>
            )}
            <div>
              <label className="block text-[10px] uppercase tracking-widest font-semibold text-[#7c5a4e] mb-1">{t("notes")}</label>
              <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full border border-[#e8dcc8] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4a1c17]" />
            </div>
            <button type="submit" disabled={booking} className="w-full rounded-lg bg-red-600 hover:bg-red-700 text-white py-3 font-bold text-sm disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="exp-confirm-btn">
              {booking && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("confirmBooking")}
              {event.price > 0 && <span className="opacity-80">· {event.price * (parseInt(form.participants) || 1)} €</span>}
            </button>
            <p className="text-[11px] text-[#7c5a4e] italic text-center">{tr(T.payOnSiteEmailNote, lang)}</p>
          </form>
        )}
      </main>
      <AppBottomBar current="none" lang={lang} />
    </div>
  );
}
